// --- Dependencies -------------------------------------------------------------------------------
import { useEffect } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import config from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import type { Lang } from "@/hooks/useLang/types";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useStore from "@/hooks/useStore";
import type { TyActiveStore } from "@/hooks/useStore/types";
import useStoreLanguages from "@/hooks/useStoreLanguages";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyTranslationJob } from "@/types";
import SettingsCard from "@/UI/components/SettingsCard";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import { orderLangs, withCacheBust } from "@/utils";
import "./styles.css";

// --- Logic --------------------------------------------------------------------------------------

/**
 * The languages the **menu** is written in — deliberately its own card, and deliberately not the
 * segmented control in Preferences, which is the dashboard's own language and a different axis.
 *
 * There is no save footer: adding and removing both go through a confirmation, and that confirmation
 * is the save gate. Nothing here is ever staged.
 */
export default function SettingsLanguages() {
    const t = useTranslation();
    const { canEdit } = usePermission();
    const { addComp, removeComp } = usePopup();
    const storeId = useStore((state) => state.storeId);
    const setStore = useStore((state) => state.setStore);
    const { languages, defaultLang } = useStoreLanguages();

    const editable = canEdit("settings");

    const { data, loading, error, invoke } = useFetch<TyApiResponse<{ store: TyActiveStore }>>("/settings/store", { method: "PUT" }, false);

    // Translation runs in the background, so its state is polled rather than returned by the save.
    const { data: jobData, invoke: refetchJob } = useFetch<TyApiResponse<{ job: TyTranslationJob | null }>>("/settings/translation");
    const job = jobData?.data?.job ?? null;
    const isJobLive = Boolean(job?.isLive);

    useEffect(() => {
        if (!isJobLive) return;
        const timer = setInterval(() => refetchJob(), 3000);
        return () => clearInterval(timer);
    }, [isJobLive]);

    useEffect(() => {
        if (!data?.success || !storeId) return;
        // Same reason as the Brand card: the logo keeps its S3 key, so it needs a version to escape the cache.
        setStore(storeId, { ...data.data.store, logo: withCacheBust(data.data.store.logo) });
        // A save may have just queued a run — ask for it now instead of waiting on the poll.
        refetchJob();
    }, [data]);

    const jobLine = (lang: Lang): string | null => {
        if (!job || job.to !== lang) return null;
        const numbers = (text: string) => text.replace("{done}", String(job.done)).replace("{total}", String(job.total));

        if (job.status === "pending" || job.status === "running") return numbers(t.settingsLanguages.translating);
        if (job.status === "waitingQuota") return numbers(t.settingsLanguages.paused);
        if (job.status === "failed") return t.settingsLanguages.failed;
        return t.settingsLanguages.finished;
    };

    const confirm = (name: string, message: string, confirmLabel: string, variant: "primary" | "danger", onConfirm: () => void) => {
        removeComp(name);
        addComp({
            name,
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: { message, confirmLabel, cancelLabel: t.settingsLanguages.cancel, variant, onConfirm },
        });
    };

    const handleAdd = (lang: Lang) =>
        confirm("add-language", t.settingsLanguages.addConfirm, t.settingsLanguages.addConfirmAction, "primary", () =>
            invoke({ data: { languages: orderLangs([...languages, lang]) } })
        );

    const handleRemove = (lang: Lang) =>
        confirm("remove-language", t.settingsLanguages.removeConfirm, t.settingsLanguages.removeConfirmAction, "danger", () =>
            invoke({ data: { languages: languages.filter((entry) => entry !== lang) } })
        );

    /** Only changes which language the menu greets customers in, so it applies without a confirmation. */
    const handleSetDefault = (lang: Lang) => invoke({ data: { defaultLang: lang } });

    return (
        <SettingsCard
            icon="/icons/language-icon.svg"
            title={t.settingsLanguages.title}
            subtitle={t.settingsLanguages.subtitle}
            badge={editable ? undefined : t.settingsLanguages.readOnlyBadge}
        >
            {/* The card body is a two-column grid — this section owns the full width of it. */}
            <div className="settings-field settings-field--full">
                <ul className="settings-languages">
                    {config.menuLanguages.map((lang) => {
                        const enabled = languages.includes(lang);
                        const isDefault = enabled && lang === defaultLang;
                        // The menu must keep a language, and the one it opens in cannot be the one removed.
                        const canRemove = enabled && !isDefault && languages.length > 1;

                        const status = isDefault
                            ? t.settingsLanguages.statusDefault
                            : enabled
                              ? t.settingsLanguages.statusEnabled
                              : t.settingsLanguages.statusDisabled;

                        return (
                            <li key={lang} className="settings-languages-row" data-enabled={enabled} data-default={isDefault}>
                                <img src={config.langFlags[lang]} className="settings-languages-flag" alt="" />

                                <div className="settings-languages-text">
                                    <p className="settings-languages-name">
                                        {t.languages[lang]}
                                        {isDefault && <span className="settings-languages-badge">{t.settingsLanguages.defaultBadge}</span>}
                                    </p>
                                    <p className="settings-languages-status">{jobLine(lang) ?? status}</p>

                                    {job?.to === lang && job.total > 0 && (job.isLive || job.status === "done") && (
                                        <div
                                            className="settings-languages-bar"
                                            role="progressbar"
                                            aria-valuenow={Math.round(((job.done + job.skipped) / job.total) * 100)}
                                            aria-valuemin={0}
                                            aria-valuemax={100}
                                        >
                                            <span style={{ width: `${Math.min(100, Math.round(((job.done + job.skipped) / job.total) * 100))}%` }} />
                                        </div>
                                    )}

                                    {job?.to === lang && !job.isLive && job.skipped > 0 && (
                                        <p className="settings-languages-warn">
                                            {t.settingsLanguages.needsWords.replace("{count}", String(job.skipped))}
                                        </p>
                                    )}
                                </div>

                                {editable && (
                                    <div className="settings-languages-actions">
                                        {!enabled && (
                                            <button
                                                type="button"
                                                className="settings-languages-btn settings-languages-btn--primary"
                                                onClick={() => handleAdd(lang)}
                                                disabled={loading}
                                            >
                                                {t.settingsLanguages.add}
                                            </button>
                                        )}
                                        {enabled && !isDefault && (
                                            <button
                                                type="button"
                                                className="settings-languages-btn"
                                                onClick={() => handleSetDefault(lang)}
                                                disabled={loading}
                                            >
                                                {t.settingsLanguages.setDefault}
                                            </button>
                                        )}
                                        {canRemove && (
                                            <button
                                                type="button"
                                                className="settings-languages-btn settings-languages-btn--danger"
                                                onClick={() => handleRemove(lang)}
                                                disabled={loading}
                                            >
                                                {t.settingsLanguages.remove}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>

                {/* Addresses and long descriptions are where machine translation reads worst, so the
                    owner is told to check them rather than left to discover it from a customer. */}
                {job && <p className="settings-languages-note">{t.settingsLanguages.reviewNote}</p>}

                <p className="settings-languages-hint">{languages.length === 1 ? t.settingsLanguages.lastOneHint : t.settingsLanguages.hint}</p>
                {error && <p className="settings-languages-error">{error}</p>}
            </div>
        </SettingsCard>
    );
}
